import React, { useState, useEffect } from 'react';
import { Shield, Plus, Users, Briefcase, Phone, X, Calendar, BarChart2, ClipboardList, MessageSquare, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal, LabeledInput } from '../../components/SharedUI';
import { getTmSupervisors, assignTmSupervisorClient, unassignTmSupervisorClient, assignTmSupervisorTeam, unassignTmSupervisorTeam, createTmTouchpoint, formatIQD, getTmSupervisorPerformance, formatSeconds } from '../../utils/tm_api';
import { getClients } from '../../utils/api';
import { getTmTeams } from '../../utils/tm_api';

export default function TmSupervisorsPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = ['owner', 'admin'].includes(user.role);
  if (!isOwner) return <div className="p-6 text-center text-[var(--text-muted)]" dir="rtl">صفحة خاصة بالمالك</div>;

  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showAssignClient, setShowAssignClient] = useState(null);
  const [showAssignTeam, setShowAssignTeam] = useState(null);
  const [showTouchpoint, setShowTouchpoint] = useState(null);
  const [performanceSv, setPerformanceSv] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  const handleShowPerformance = async (sv) => {
    try {
      setPerformanceSv(sv);
      setPerfLoading(true);
      setPerfData(null);
      const res = await getTmSupervisorPerformance(sv.employee_id);
      setPerfData(res);
    } catch (e) {
      toast.error('خطأ في تحميل تفاصيل الأداء');
      setPerformanceSv(null);
    } finally {
      setPerfLoading(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setSupervisors(await getTmSupervisors());
    } catch (e) { toast.error('خطأ في التحميل'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    getClients().then(d => setClients(Array.isArray(d) ? d : d.clients || [])).catch(() => {});
    getTmTeams().then(setTeams).catch(() => {});
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--text-dark-h)]">إدارة المتابعين</h1>
          <p className="text-sm text-[var(--text-muted)]">{supervisors.length} متابع</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
      ) : supervisors.length === 0 ? (
        <div className="text-center py-12 text-[var(--text-muted)]">
          <p>لا يوجد متابعين</p>
          <p className="text-xs mt-2">أنشئ مستخدمين بدور "supervisor" من صفحة الإعدادات</p>
        </div>
      ) : (
        <div className="space-y-6">
          {supervisors.map(sv => (
            <div key={sv.user_id} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-[var(--border-light)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-primary flex items-center justify-center text-white font-bold">
                    {sv.employee_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <h3 className="font-bold text-[var(--text-dark-h)]">{sv.employee_name || sv.full_name}</h3>
                    <p className="text-xs text-[var(--text-muted)]">@{sv.username}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-left sm:text-right">
                    <p className="text-sm font-bold text-green-600 dark:text-green-400">{formatIQD(sv.current_month_commissions)}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">العمولات هذا الشهر</p>
                  </div>
                  <button 
                    onClick={() => handleShowPerformance(sv)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition duration-200"
                  >
                    عرض تفاصيل الأداء والنشاط 📊
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Clients */}
                <div className="p-3 rounded-xl bg-[var(--bg-flat-50)]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-[var(--text-gray)]"><Briefcase className="w-3.5 h-3.5 inline ml-1" /> العملاء</h4>
                    <button onClick={() => setShowAssignClient(sv)} className="text-xs text-primary font-bold hover:underline">
                      <Plus className="w-3 h-3 inline" /> ربط
                    </button>
                  </div>
                  {sv.clients.length === 0 ? (
                    <p className="text-xs text-[var(--text-muted)]">لم يتم ربط عملاء</p>
                  ) : (
                    <div className="space-y-1">
                      {sv.clients.map(c => (
                        <div key={c.id} className="flex items-center justify-between text-xs">
                          <span className="text-[var(--text-dark-h)]">{c.client_name}</span>
                          <button onClick={async () => { await unassignTmSupervisorClient(sv.employee_id, c.client_id); toast.success('تم'); load(); }}
                            className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Teams */}
                <div className="p-3 rounded-xl bg-[var(--bg-flat-50)]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-[var(--text-gray)]"><Users className="w-3.5 h-3.5 inline ml-1" /> الفرق</h4>
                    <button onClick={() => setShowAssignTeam(sv)} className="text-xs text-primary font-bold hover:underline">
                      <Plus className="w-3 h-3 inline" /> ربط
                    </button>
                  </div>
                  {sv.teams.length === 0 ? (
                    <p className="text-xs text-[var(--text-muted)]">لم يتم ربط فرق</p>
                  ) : (
                    <div className="space-y-1">
                      {sv.teams.map(t => (
                        <div key={t.id} className="flex items-center justify-between text-xs">
                          <span className="text-[var(--text-dark-h)]">{t.team_name} <span className="text-[var(--text-muted)]">({t.leader_name})</span></span>
                          <button onClick={async () => { await unassignTmSupervisorTeam(sv.employee_id, t.team_id); toast.success('تم'); load(); }}
                            className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Client Modal */}
      {showAssignClient && (
        <Modal title={`ربط عميل — ${showAssignClient.employee_name}`} onClose={() => setShowAssignClient(null)}>
          <div className="space-y-2">
            {clients.map(c => (
              <button key={c.id} onClick={async () => {
                try { await assignTmSupervisorClient(showAssignClient.employee_id, { client_id: c.id }); toast.success('تم الربط'); setShowAssignClient(null); load(); }
                catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
              }}
                className="w-full text-right p-3 rounded-xl border border-[var(--border-light)] hover:bg-primary/5 transition text-sm text-[var(--text-dark-h)]">
                {c.name}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* Assign Team Modal */}
      {showAssignTeam && (
        <Modal title={`ربط فريق — ${showAssignTeam.employee_name}`} onClose={() => setShowAssignTeam(null)}>
          <div className="space-y-2">
            {teams.map(t => (
              <button key={t.id} onClick={async () => {
                try { await assignTmSupervisorTeam(showAssignTeam.employee_id, { team_id: t.id }); toast.success('تم الربط'); setShowAssignTeam(null); load(); }
                catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
              }}
                className="w-full text-right p-3 rounded-xl border border-[var(--border-light)] hover:bg-primary/5 transition text-sm text-[var(--text-dark-h)]">
                {t.name} <span className="text-[var(--text-muted)]">({t.leader_name})</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* Supervisor Performance Modal */}
      {performanceSv && (
        <Modal 
          title={`تقرير أداء ونشاط المتابع — ${performanceSv.employee_name || performanceSv.full_name}`} 
          onClose={() => setPerformanceSv(null)}
          width={800}
        >
          {perfLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-[var(--text-muted)] font-medium">جاري تحميل وتحليل بيانات الأداء...</p>
            </div>
          ) : perfData ? (
            <div className="space-y-6 text-right" dir="rtl">
              {/* Header Info */}
              <div className="p-4 rounded-2xl bg-gradient-to-l from-primary/10 to-transparent border border-primary/20 flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-[var(--text-dark-h)]">{perfData.supervisor.employee_name || perfData.supervisor.full_name}</h4>
                  <p className="text-xs text-[var(--text-muted)]">معرّف المستخدم: @{perfData.supervisor.username}</p>
                </div>
                <div className="px-3 py-1 bg-primary/20 text-primary text-xs font-bold rounded-full">
                  متابع نشط
                </div>
              </div>

              {/* Statistics Grid */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-[var(--text-dark-h)] flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-primary" /> إحصائيات الأداء العامة
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 rounded-xl bg-[var(--bg-flat-50)] border border-[var(--card-border)] space-y-1">
                    <div className="flex items-center justify-between">
                      <Briefcase className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-[var(--text-muted)]">العملاء</span>
                    </div>
                    <p className="text-xl font-black text-[var(--text-dark-h)]">{perfData.stats.total_clients}</p>
                  </div>

                  <div className="p-4 rounded-xl bg-[var(--bg-flat-50)] border border-[var(--card-border)] space-y-1">
                    <div className="flex items-center justify-between">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-[var(--text-muted)]">الفرق</span>
                    </div>
                    <p className="text-xl font-black text-[var(--text-dark-h)]">{perfData.stats.total_teams}</p>
                  </div>

                  <div className="p-4 rounded-xl bg-[var(--bg-flat-50)] border border-[var(--card-border)] space-y-1">
                    <div className="flex items-center justify-between">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-[var(--text-muted)]">العمولات</span>
                    </div>
                    <p className="text-lg font-black text-green-600 dark:text-green-400">{formatIQD(perfData.stats.total_commissions)}</p>
                  </div>

                  <div className="p-4 rounded-xl bg-[var(--bg-flat-50)] border border-[var(--card-border)] space-y-1">
                    <div className="flex items-center justify-between">
                      <MessageSquare className="w-4 h-4 text-amber-500" />
                      <span className="text-xs text-[var(--text-muted)]">التواصل</span>
                    </div>
                    <p className="text-xl font-black text-[var(--text-dark-h)]">{perfData.stats.total_touchpoints}</p>
                  </div>
                </div>
              </div>

              {/* Tasks Statistics Sub-Grid */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-[var(--text-dark-h)] flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-primary" /> حالة المهام المتابعة
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 space-y-1">
                    <span className="text-[10px] text-[var(--text-muted)]">إجمالي المهام</span>
                    <p className="text-lg font-bold text-[var(--text-dark-h)]">{perfData.stats.total_tasks}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30 space-y-1">
                    <span className="text-[10px] text-green-600 dark:text-green-400">المهام المقبولة ✅</span>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{perfData.stats.accepted_tasks}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 space-y-1">
                    <span className="text-[10px] text-blue-600 dark:text-blue-400">المهام النشطة</span>
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{perfData.stats.active_tasks}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 space-y-1">
                    <span className="text-[10px] text-red-600 dark:text-red-400">المهام الملغاة</span>
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">{perfData.stats.cancelled_tasks}</p>
                  </div>
                </div>
              </div>

              {/* Client List Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-[var(--text-dark-h)] flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-primary" /> قائمة العملاء ومؤشرات التفاعل
                </h3>
                {perfData.clients.length === 0 ? (
                  <div className="text-center py-6 bg-[var(--bg-flat-50)] rounded-2xl text-xs text-[var(--text-muted)] border border-dashed border-[var(--card-border)]">
                    لا يوجد عملاء مرتبطين بهذا المتابع حالياً.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {perfData.clients.map(c => (
                      <div key={c.client_id} className="p-4 rounded-2xl bg-[var(--card-bg)] border border-[var(--card-border)] hover:shadow-md transition-shadow duration-200 flex flex-col justify-between gap-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-bold text-sm text-[var(--text-dark-h)]">{c.client_name}</h4>
                            {c.client_phone && (
                              <a 
                                href={`tel:${c.client_phone}`} 
                                className="text-xs text-[var(--text-muted)] hover:text-primary transition flex items-center gap-1 mt-1"
                              >
                                <Phone className="w-3 h-3" /> {c.client_phone}
                              </a>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            c.client_status === 'active' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {c.client_status === 'active' ? 'نشط' : 'غير نشط'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[var(--border-light)] text-xs">
                          <div className="flex-1 bg-[var(--bg-flat-50)] px-2.5 py-1.5 rounded-lg flex items-center justify-between">
                            <span className="text-[var(--text-muted)] text-[10px]">📋 المهام</span>
                            <span className="font-bold text-[var(--text-dark-h)]">{c.task_count}</span>
                          </div>
                          <div className="flex-1 bg-[var(--bg-flat-50)] px-2.5 py-1.5 rounded-lg flex items-center justify-between">
                            <span className="text-[var(--text-muted)] text-[10px]">💬 التفاعل</span>
                            <span className="font-bold text-amber-600 dark:text-amber-400">{c.touchpoint_count}</span>
                          </div>
                        </div>

                        {/* Subscription details in performance modal */}
                        {c.sub_id ? (
                          <div className="mt-2 p-3 rounded-xl bg-blue-50/40 dark:bg-blue-950/10 border border-blue-100/50 dark:border-blue-950/20 text-[11px] space-y-1.5 w-full">
                            <div className="flex items-center justify-between font-bold text-blue-700 dark:text-blue-400">
                              <span>📦 باقة {c.package_name}</span>
                              <span className="text-[9px] opacity-75">ينتهي: {c.sub_period_end}</span>
                            </div>
                            <div className="flex justify-between text-[9px] text-[var(--text-muted)]">
                              <span>مستهلك: {formatSeconds(c.sub_used_seconds)}</span>
                              <span>متبقي: {formatSeconds(c.sub_remaining_seconds)}</span>
                            </div>
                            <div className="w-full h-1.5 bg-blue-100 dark:bg-blue-950 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full" 
                                style={{ width: `${Math.min(100, Math.round(((c.sub_used_seconds || 0) / (c.sub_total_seconds || 1)) * 100))}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 p-2 rounded-xl bg-amber-50/40 dark:bg-amber-950/10 border border-amber-100/50 dark:border-amber-950/20 text-[10px] text-amber-700 dark:text-amber-400 font-medium text-center w-full">
                            ⚠️ لا يوجد باقة اشتراك نشطة
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Touchpoints Timeline Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-[var(--text-dark-h)] flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" /> سجل وجدول النشاط والتواصل
                </h3>
                {perfData.touchpoints.length === 0 ? (
                  <div className="text-center py-10 bg-[var(--bg-flat-50)] rounded-2xl text-xs text-[var(--text-muted)] border border-dashed border-[var(--card-border)]">
                    لم يقم المتابع بتسجيل أي تواصل مع العملاء حتى الآن.
                  </div>
                ) : (
                  <div className="relative border-r-2 border-primary/20 pr-4 mr-2 space-y-4 py-2">
                    {perfData.touchpoints.map((tp, idx) => (
                      <div key={tp.id || idx} className="relative group">
                        {/* Timeline Circle Marker */}
                        <div className="absolute top-1 -right-[23px] w-3 h-3 rounded-full bg-primary border-2 border-[var(--card-bg)] transition-transform group-hover:scale-125 duration-200"></div>
                        
                        {/* Touchpoint Card */}
                        <div className="p-4 rounded-2xl bg-[var(--card-bg)] border border-[var(--card-border)] hover:border-primary/30 transition-all duration-200 space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[var(--text-dark-h)] text-sm">{tp.client_name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                tp.contact_type === 'whatsapp' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                tp.contact_type === 'call' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                tp.contact_type === 'meeting' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                                'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                              }`}>
                                {tp.contact_type === 'whatsapp' ? 'واتساب 💬' :
                                 tp.contact_type === 'call' ? 'مكالمة هاتفية 📞' :
                                 tp.contact_type === 'meeting' ? 'اجتماع 🤝' : 'آخر'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-[var(--text-muted)]">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{tp.contact_date}</span>
                            </div>
                          </div>

                          {tp.notes && (
                            <p className="text-xs text-[var(--text-dark-h)] bg-[var(--bg-flat-50)] p-3 rounded-xl leading-relaxed whitespace-pre-wrap">
                              {tp.notes}
                            </p>
                          )}

                          {tp.next_contact_date && (
                            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-lg w-max">
                              <Calendar className="w-3 h-3" />
                              <span>التواصل القادم المقرر: {tp.next_contact_date}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-red-500 text-sm font-semibold">فشل تحميل بيانات الأداء. الرجاء المحاولة مرة أخرى.</div>
          )}
        </Modal>
      )}
    </div>
  );
}
